import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, RefreshCw } from 'lucide-react';
import { HitApi } from '../../Api/ApiHit';
import { searchKiosk } from '../../constant/Constant';
import { getSelectedUnit } from '../../storage/Storage';
import KioskCard from './KioskCard';

const KioskManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kiosks, setKiosks] = useState([]);

  useEffect(() => {
    fetchKiosks();
  }, []);

  const fetchKiosks = async () => {
    try {
      setLoading(true);

      // Get unitId from selected unit
      const selectedUnit = getSelectedUnit();
      const unitId = selectedUnit?.unitIds || selectedUnit?._id;

      // Prepare request payload
      const requestPayload = {
        page: 1,
        limit: 50,
        search: {
          unitId: unitId,
          isActive: true
        }
      };

      console.log('📤 Search Kiosk Request Payload:', requestPayload);

      // Call API
      const response = await HitApi(requestPayload, searchKiosk);

      console.log('📡 Search Kiosk API Response:', response);

      if (response.success && response.data) {
        setKiosks(response.data);
        console.log('✅ Kiosks loaded:', response.data.length);
      }
    } catch (error) {
      console.error('❌ Error fetching kiosks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKioskClick = (kiosk) => {
    console.log('🖱️ Kiosk clicked:', kiosk);
    navigate(`/kiosk-management/dashboard/${kiosk._id}`, { state: { kiosk } });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Monitor className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Registered Kiosks
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                View and manage your registered kiosk devices
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Kiosks</p>
                <p className="text-2xl font-bold text-gray-900">{kiosks.length}</p>
              </div>
              <div className="h-12 w-px bg-gray-200"></div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Online</p>
                <p className="text-2xl font-bold text-green-600">
                  {kiosks.filter(k => k.isOnline).length}
                </p>
              </div>
              <div className="h-12 w-px bg-gray-200"></div>
              <div>
                <p className="text-sm text-gray-500 font-medium">Offline</p>
                <p className="text-2xl font-bold text-gray-400">
                  {kiosks.filter(k => !k.isOnline).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Loading kiosks...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && kiosks.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center border border-gray-100">
            <Monitor className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No Kiosks Found
            </h3>
            <p className="text-gray-600">
              No registered kiosks found for this unit.
            </p>
          </div>
        )}

        {/* Kiosk Cards Grid */}
        {!loading && kiosks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {kiosks.map((kiosk) => (
              <KioskCard key={kiosk._id} kiosk={kiosk} onClick={handleKioskClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskManagement;
